import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

export interface SaasRouteDialogData {
  stack: string;
  containerName: string;
  customerId: string;
  backendIp: string | null;
  backendPort: number | null;
}

export interface SaasRouteDialogResult {
  customerId: string;
  backendIp: string;
  backendPort: number;
  pb40Token: string;
}

@Component({
  selector: 'app-saas-route-dialog',
  standalone: true,
  imports: [FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule],
  template: `
    <div class="dialog-heading">
      <span class="dialog-icon"><mat-icon>route</mat-icon></span>
      <div>
        <h2 mat-dialog-title>Route konfigurieren</h2>
        <p>{{ data.containerName }}</p>
      </div>
    </div>
    <mat-dialog-content>
      <div class="route-context">
        <span>Docker-Stack</span>
        <strong>{{ data.stack }}</strong>
      </div>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Customer-ID / Subdomain</mat-label>
        <mat-icon matPrefix>language</mat-icon>
        <input matInput [(ngModel)]="customerId" #customerIdControl="ngModel" maxlength="63"
          pattern="^[a-z0-9][a-z0-9-]{0,62}$" autocomplete="off" autocapitalize="none" spellcheck="false"
          cdkFocusInitial>
        <mat-hint>Wird als Subdomain verwendet.</mat-hint>
        @if (customerIdControl.invalid && customerIdControl.touched) {
        <mat-error>Nur Kleinbuchstaben, Zahlen und Bindestriche sind erlaubt.</mat-error>
        }
      </mat-form-field>
      <div class="field-row">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Backend-IP</mat-label>
          <mat-icon matPrefix>dns</mat-icon>
          <input matInput [(ngModel)]="backendIp" #backendIpControl="ngModel" inputmode="decimal" autocomplete="off"
            pattern="^(?:[0-9]{1,3}\\.){3}[0-9]{1,3}$">
          @if (backendIpControl.invalid && backendIpControl.touched) {
          <mat-error>Geben Sie eine gültige IPv4-Adresse ein.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Backend-Port</mat-label>
          <mat-icon matPrefix>settings_ethernet</mat-icon>
          <input matInput type="number" [(ngModel)]="backendPort" min="1" max="65535" step="1" autocomplete="off">
        </mat-form-field>
      </div>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>PB40-Token</mat-label>
        <mat-icon matPrefix>key</mat-icon>
        <input matInput type="password" [(ngModel)]="token" autocomplete="off" spellcheck="false"
          (keydown.enter)="submit()">
      </mat-form-field>
      <small><mat-icon>lock</mat-icon>Der Token wird nur für diese Anfrage verwendet und nicht gespeichert.</small>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close()">Abbrechen</button>
      <button mat-flat-button color="primary" type="button" [disabled]="!canSubmit" (click)="submit()">
        <mat-icon>route</mat-icon>Route speichern
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host { display: block; color: var(--app-surface-dark, #1e293b); }
    .dialog-heading { display: flex; gap: .9rem; padding: 1.5rem 1.5rem .4rem; }
    .dialog-icon { display: grid; place-items: center; flex: 0 0 2.6rem; height: 2.6rem; border-radius: .75rem; color: #fff; background: var(--app-primary, #145a7d); }
    h2[mat-dialog-title] { margin: 0; padding: 0; font-size: 1.2rem; font-weight: 600; }
    .dialog-heading p { margin: .25rem 0 0; color: var(--app-surface-light, #64748b); line-height: 1.45; }
    mat-dialog-content { display: grid; gap: 1rem; min-width: min(31rem, 76vw); padding-top: 1rem; }
    .route-context { display: flex; justify-content: space-between; gap: 1rem; padding: .75rem .9rem; border: 1px solid rgba(100, 116, 139, .25); border-radius: .65rem; background: rgba(148, 163, 184, .08); }
    .route-context span { color: var(--app-surface-light, #64748b); }
    .route-context strong { overflow-wrap: anywhere; font-weight: 600; }
    .field-row { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(8rem, .65fr); gap: .8rem; }
    mat-form-field { width: 100%; }
    small { display: flex; align-items: center; gap: .35rem; margin-top: -.45rem; color: var(--app-surface-light, #64748b); line-height: 1.45; }
    small mat-icon { width: 1rem; height: 1rem; font-size: 1rem; }
    mat-dialog-actions { padding: .75rem 1.5rem 1.25rem; }
    @media (max-width: 560px) { .dialog-heading { padding: 1.1rem 1rem .25rem; } mat-dialog-content { min-width: 0; padding: .8rem 1rem; } .field-row { grid-template-columns: 1fr; } mat-dialog-actions { padding: .6rem 1rem 1rem; } }
  `]
})
export class SaasRouteDialogComponent {
  customerId: string;
  backendIp: string;
  backendPort: number | null;
  token = '';

  constructor(
    readonly dialogRef: MatDialogRef<SaasRouteDialogComponent, SaasRouteDialogResult>,
    @Inject(MAT_DIALOG_DATA) readonly data: SaasRouteDialogData
  ) {
    this.customerId = data.customerId;
    this.backendIp = data.backendIp ?? '';
    this.backendPort = data.backendPort;
  }

  get canSubmit(): boolean {
    return this.isValidCustomerId(this.customerId.trim())
      && this.isValidIpv4(this.backendIp.trim())
      && Number.isInteger(this.backendPort)
      && Number(this.backendPort) >= 1
      && Number(this.backendPort) <= 65535
      && this.token.trim().length > 0;
  }

  submit(): void {
    if (!this.canSubmit || this.backendPort === null) return;
    this.dialogRef.close({
      customerId: this.customerId.trim(),
      backendIp: this.backendIp.trim(),
      backendPort: Number(this.backendPort),
      pb40Token: this.token.trim()
    });
  }

  private isValidCustomerId(value: string): boolean {
    return /^[a-z0-9][a-z0-9-]{0,62}$/.test(value);
  }

  private isValidIpv4(value: string): boolean {
    const parts = value.split('.');
    return parts.length === 4 && parts.every(part => /^\d{1,3}$/.test(part) && Number(part) >= 0 && Number(part) <= 255);
  }
}
